'use client';

import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import { useUpdateStepper } from '@nrdy-marketing-engine/stepper';
import { createContext, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { CartContextInterface } from '../@types/cart-context';
import type { CartState } from '../@types/cart-state';
import { CartStep } from '../@types/cart-step';
import useCartController from './hooks/use-cart-controller';
import useCartStepper from './hooks/use-cart-stepper';
import useConvertUserToClient from './hooks/use-convert-user-to-client';
import useFetchClientData from './hooks/use-fetch-client-data';

export const useCartContext = (): CartContextInterface => {
  const context = useContext(CartContext);

  return context;
};

export const initialCartState: CartState = {
  client: {
    clientId: '',
    countryCode: 'US',
    email: '',
    firstName: '',
    id: '',
    jurisdictionId: 1,
    lastName: '',
    password: '',
  },
  contracts: [],
  phoneNumber: '',
  zipCode: '',
};

export const CartContext = createContext<CartContextInterface>({
  applyPromoCode: async () => {},
  cartState: initialCartState,
  cartStep: CartStep.Account,
  originalCatalogItem: null,
  removePromoCode: () => {},
  useFormVals: null,
});

const useFormState = () => {
  const useFormVals = useForm<CartState>({
    defaultValues: initialCartState,
    mode: 'onTouched',
  });
  const { watch, setValue } = useFormVals;
  const zipCode = watch('zipCode');
  const phoneNumber = watch('phoneNumber');

  useFetchClientData(setValue);

  return { phoneNumber, useFormVals, zipCode };
};

const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const { setStep } = useUpdateStepper(3, 0);
  const { convertUserToClientState } = useConvertUserToClient();

  const { contracts } = useUserContext();
  const {
    cartLoading,
    cartState,
    applyPromoCode,
    removePromoCode,
    missingCatalogItem,
    originalCatalogItem,
    catalogItemError,
  } = useCartController();
  const { useFormVals, zipCode, phoneNumber } = useFormState();
  const { cartStep } = useCartStepper({
    cartLoading,
    catalogItemPresent: !!originalCatalogItem,
    convertUserToClientState,
    missingCatalogItem,
    catalogItemError,
    onChangeStep: setStep,
  });

  return (
    <CartContext.Provider
      value={{
        applyPromoCode,
        cartState: {
          ...cartState,
          contracts,
          phoneNumber,
          zipCode,
        },
        cartStep,
        originalCatalogItem,
        removePromoCode,
        useFormVals,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
